import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { NextActionFeedItem } from '@cdf/api-client-react';
import { AlertTriangleIcon, CopyIcon, CheckIcon, SendIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';

/**
 * Draft review sheet for approving an email-type next action.
 *
 * Right-side drawer. The user reviews the
 * AI-prepared recipient/subject/body, edits as needed, and approves to send
 * through the connected Microsoft 365 mailbox. When authority configuration
 * blocks automated sending (human_only), the draft is offered read-only with
 * a copy button instead of a send button.
 */

type DraftReviewSheetProps = {
  action: NextActionFeedItem & { draft: { to: string; subject: string; body: string } };
  sending: boolean;
  error: string | null;
  humanOnlyMessage: string | null;
  onApprove: (input: { to: string; subject: string; body: string }) => void;
  onDismiss: () => void;
  onClose: () => void;
};

export function DraftReviewSheet({
  action,
  sending,
  error,
  humanOnlyMessage,
  onApprove,
  onDismiss,
  onClose
}: DraftReviewSheetProps) {
  const [subject, setSubject] = useState(action.draft.subject);
  const [body, setBody] = useState(action.draft.body);
  const [copied, setCopied] = useState(false);

  const humanOnly = Boolean(humanOnlyMessage);
  const entityName = action.contactName ?? action.companyName ?? null;
  const canSend = subject.trim().length > 0 && body.trim().length > 0;

  const copyDraft = async () => {
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Sheet open onOpenChange={(next) => { if (!next && !sending) onClose(); }}>
      <SheetContent
        side="right"
        className="bg-background flex h-dvh w-full flex-col gap-4 overflow-y-auto p-4 pb-6 sm:max-w-xl sm:p-6"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="flex flex-wrap items-center gap-2">
            <Badge variant={action.priority >= 80 ? 'default' : 'secondary'}>
              Priority {action.priority}
            </Badge>
            <span className="leading-snug break-words">Review &amp; approve</span>
          </SheetTitle>
          <SheetDescription className="break-words">{action.title}</SheetDescription>
        </SheetHeader>

        <div className="text-muted-foreground flex flex-col gap-1 text-sm">
          <p className="break-words">{action.rationale}</p>
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {entityName && action.deepLink && (
              <Link
                to={action.deepLink}
                className="hover:text-foreground underline decoration-dotted underline-offset-2"
                onClick={onClose}
              >
                {entityName}
              </Link>
            )}
            {action.sourceKind && <span>Source: {action.sourceKind} signal</span>}
          </p>
        </div>

        {humanOnly && (
          <div
            className="text-destructive flex items-start gap-2 rounded-md border p-3 text-sm"
            role="alert"
          >
            <AlertTriangleIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span className="break-words">{humanOnlyMessage}</span>
          </div>
        )}

        {error && (
          <div
            className="text-destructive flex items-start gap-2 rounded-md border p-3 text-sm"
            role="alert"
          >
            <AlertTriangleIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span className="break-words">{error}</span>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <div className="flex items-baseline justify-between gap-2">
            <Label htmlFor="next-action-to" className="text-muted-foreground">To</Label>
            <span className="text-foreground truncate text-sm" id="next-action-to">
              {action.draft.to}
            </span>
          </div>

          <Label htmlFor="next-action-subject">Subject</Label>
          <Input
            id="next-action-subject"
            className="min-h-11"
            value={subject}
            readOnly={humanOnly}
            onChange={(event) => setSubject(event.target.value)}
            maxLength={300}
          />

          <div className="flex items-center justify-between gap-2">
            <Label htmlFor="next-action-body">Message</Label>
            {humanOnly && (
              <Button
                type="button"
                variant="outline"
                className="h-9 px-3 text-xs"
                onClick={() => void copyDraft()}
              >
                {copied ? <CheckIcon className="size-3.5" aria-hidden /> : <CopyIcon className="size-3.5" aria-hidden />}
                {copied ? 'Copied' : 'Copy draft'}
              </Button>
            )}
          </div>
          <Textarea
            id="next-action-body"
            className="min-h-44 text-base"
            value={body}
            readOnly={humanOnly}
            onChange={(event) => setBody(event.target.value)}
            maxLength={20000}
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {humanOnly ? (
            <Button className="h-12 min-w-0 flex-1" onClick={onClose}>
              Done
            </Button>
          ) : (
            <Button
              className="h-12 min-w-0 flex-1"
              disabled={sending || !canSend}
              onClick={() => onApprove({ to: action.draft.to, subject, body })}
            >
              <SendIcon className="size-4" aria-hidden />
              {sending ? 'Sending…' : 'Approve & send'}
            </Button>
          )}
          <Button
            variant="outline"
            className="h-12 min-w-0 flex-1 sm:flex-none sm:px-6"
            disabled={sending}
            onClick={onDismiss}
          >
            Dismiss
          </Button>
          {!humanOnly && (
            <Button
              variant="ghost"
              className="h-12 min-w-0 flex-1 sm:flex-none sm:px-6"
              disabled={sending}
              onClick={onClose}
            >
              Cancel
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
